import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '../design';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  RefreshCw, CheckCircle, CalendarClock, CalendarX2, Zap, Wallet,
  TrendingDown, HeartPulse, Clock, FileX2, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';

const statusTone = (s) => {
  const v = String(s || '').toLowerCase();
  if (v === 'active') return 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30';
  if (v === 'past_due' || v === 'pending') return 'bg-amber-500/15 text-amber-500 border-amber-500/30';
  if (v === 'cancelled' || v === 'suspended' || v === 'halted') return 'bg-red-500/15 text-red-500 border-red-500/30';
  return 'bg-slate-500/15 text-slate-400 border-slate-500/30';
};

const BUCKETS = [
  { key: 'expiring_soon', label: 'Expiring soon (≤7d)', icon: CalendarClock, tone: 'text-amber-400' },
  { key: 'expired', label: 'Expired / lapsed', icon: CalendarX2, tone: 'text-red-400' },
  { key: 'unpaid_but_live', label: 'Unpaid but LIVE', icon: Zap, tone: 'text-red-400' },
  { key: 'paid_zero_balance', label: 'Paid plan at zero', icon: Wallet, tone: 'text-amber-400' },
  { key: 'negative_balance', label: 'Negative balance', icon: TrendingDown, tone: 'text-red-400' },
  { key: 'stale_suspended', label: 'Half-suspended drift', icon: HeartPulse, tone: 'text-rose-400' },
  { key: 'stale_pending_upgrade', label: 'Stale pending upgrade', icon: Clock, tone: 'text-slate-400' },
];

function BucketTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Account</TableHead>
          <TableHead>Plan</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Balance</TableHead>
          <TableHead>Expiry</TableHead>
          <TableHead>Recommended action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={`${r.tenant_id}-${i}`} data-testid={`audit-row-${r.tenant_id}`}>
            <TableCell>
              <div className="gov-text-primary font-medium">{r.name}</div>
              <div className="gov-text-tertiary text-xs">{r.email || r.tenant_id}</div>
            </TableCell>
            <TableCell className="gov-text-secondary text-xs">{r.plan_id || '—'}</TableCell>
            <TableCell>
              <span className={`rounded border px-2 py-0.5 text-xs ${statusTone(r.billing_status)}`}>
                {r.billing_status || '—'}
              </span>
              {r.execution_mode === 'LIVE' && (
                <span className="ml-1 rounded border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 text-[10px] text-red-400">LIVE</span>
              )}
            </TableCell>
            <TableCell>
              <span className={`text-sm ${r.balance_usd < 0 ? 'text-red-400' : 'gov-text-secondary'}`}>
                {typeof r.balance_credits === 'number' ? `${r.balance_credits.toLocaleString()} Cr` : '—'}
              </span>
            </TableCell>
            <TableCell className="gov-text-tertiary text-xs whitespace-nowrap">
              {r.expires_at ? String(r.expires_at).slice(0, 10) : '—'}
              {typeof r.days_left === 'number' && <span className="ml-1 text-amber-400">({r.days_left}d)</span>}
              {typeof r.overdue_days === 'number' && (
                <span className={`ml-1 ${r.in_grace ? 'text-amber-400' : 'text-red-400'}`}>
                  {r.in_grace ? `grace ${r.overdue_days}d` : `+${r.overdue_days}d`}
                </span>
              )}
              {r.pending_since && <span className="ml-1">since {String(r.pending_since).slice(0, 10)}</span>}
            </TableCell>
            <TableCell className="gov-text-secondary text-xs max-w-xs">{r.recommended_action}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function BillingAudit() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await governanceAPI.getBillingAudit();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load billing audit', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const buckets = data?.buckets || {};

  return (
    <div className="space-y-6" data-testid="billing-audit-page">
      <PageHeader
        eyebrow="Billing Ops"
        title="Billing Audit"
        subtitle="Read-only money-risk report across all tenants. Nothing here changes data — it shows who is about to lapse, who is running unpaid/negative, and the best-practice next step for each."
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="billing-audit-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: 'Tenants scanned', value: summary.tenants_scanned ?? '—', testid: 'stat-scanned' },
          { label: 'Expiring soon', value: summary.expiring_soon ?? '—', testid: 'stat-expiring' },
          { label: 'Expired', value: summary.expired ?? '—', testid: 'stat-expired' },
          { label: 'Unpaid but LIVE', value: summary.unpaid_but_live ?? '—', testid: 'stat-unpaid-live' },
          { label: 'Paid at zero', value: summary.paid_zero_balance ?? '—', testid: 'stat-paid-zero' },
          { label: 'Negative balance', value: summary.negative_balance ?? '—', testid: 'stat-negative' },
          { label: 'Phantom invoices', value: summary.phantom_invoices ?? '—', testid: 'stat-phantom' },
          {
            label: 'Lifetime write-off',
            value: typeof summary.lifetime_writeoff_credits === 'number'
              ? `${summary.lifetime_writeoff_credits.toLocaleString()} Cr` : '—',
            testid: 'stat-writeoff',
          },
        ].map((s) => (
          <Card key={s.label} className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]">
            <CardContent className="p-4">
              <div className="gov-text-tertiary text-xs uppercase tracking-wide">{s.label}</div>
              <div className="gov-text-primary mt-1 text-2xl font-semibold" data-testid={s.testid}>{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {data?.grace_days != null && (
        <p className="gov-text-tertiary text-xs" data-testid="grace-note">
          Policy: prepaid hard-stop (balance never below 0) · {data.grace_days}-day grace after a failed renewal,
          then Free. Report generated {data.generated_at ? String(data.generated_at).slice(0, 19).replace('T', ' ') : ''} UTC.
        </p>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
      ) : (
        BUCKETS.map(({ key, label, icon: Icon, tone }) => {
          const rows = buckets[key] || [];
          return (
            <Card key={key} className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]" data-testid={`bucket-${key}`}>
              <CardHeader>
                <CardTitle className="gov-text-primary flex items-center gap-2 text-lg">
                  <Icon className={`h-5 w-5 ${tone}`} /> {label}
                  <span className="gov-text-tertiary text-sm font-normal">({rows.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rows.length === 0 ? (
                  <div className="flex items-center gap-2 py-4 gov-text-tertiary text-sm" data-testid={`bucket-${key}-empty`}>
                    <CheckCircle className="h-4 w-4 text-emerald-500" /> Nothing here — all clear.
                  </div>
                ) : (
                  <BucketTable rows={rows} />
                )}
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Phantom invoices */}
      {!loading && (data?.phantom_invoices?.length > 0) && (
        <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]" data-testid="bucket-phantom-invoices">
          <CardHeader>
            <CardTitle className="gov-text-primary flex items-center gap-2 text-lg">
              <FileX2 className="h-5 w-5 text-amber-400" /> Phantom invoices
              <span className="gov-text-tertiary text-sm font-normal">({data.phantom_invoices.length})</span>
            </CardTitle>
            <CardDescription className="gov-text-secondary">
              Invoices marked paid but whose payment id is not a real <code>pay_</code> charge. Fix via Billing Drift → Void invoices.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Payment id</TableHead>
                  <TableHead>Period</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.phantom_invoices.map((inv, i) => (
                  <TableRow key={`${inv.invoice_number}-${i}`}>
                    <TableCell className="gov-text-tertiary text-xs">{inv.tenant_id}</TableCell>
                    <TableCell className="gov-text-primary text-xs">{inv.invoice_number}</TableCell>
                    <TableCell><code className="text-amber-400 text-xs">{inv.payment_id || '(none)'}</code></TableCell>
                    <TableCell className="gov-text-tertiary text-xs">{inv.period_key || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
